import React, { useState, useRef, useContext, createContext } from "react";
import * as fabric from "fabric";
import QRCode from "qrcode";

const EditorContext = createContext();

const EditorProvider = ({ children }) => {
  const [lang, setLang] = useState("hi");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeObject, setActiveObject] = useState(null);
  const fabricCanvasRef = useRef(null);

  const [textStyle, setTextStyle] = useState({
    fill: "#000000",
    fontSize: 30,
    fontWeight: "500",
  });

  const syncStyleFromObject = (obj) => {
    if (obj && obj.type === "i-text") {
      setTextStyle({
        fill: obj.fill,
        fontSize: obj.fontSize,
        fontWeight: obj.fontWeight,
      });
    }
  };

  const Language = (e) => setLang(e.target.value);

  const registerCanvas = (canvas) => {
    fabricCanvasRef.current = canvas;
    if (!canvas) {
      setActiveObject(null);
      return;
    }

    const syncActive = () => {
      const obj = canvas.getActiveObject() || null;
      setActiveObject(obj);
      if (obj && obj.type === "i-text") {
        setText(obj.text);
        syncStyleFromObject(obj);
      }
    };

    canvas.on("selection:created", syncActive);
    canvas.on("selection:updated", syncActive);
    canvas.on("selection:cleared", () => setActiveObject(null));

    canvas.on("object:modified", () => {
      const obj = canvas.getActiveObject();
      setActiveObject(obj || null);
      syncStyleFromObject(obj);
    });

    canvas.on("object:scaling", (e) => {
      const obj = e.target;
      if (obj && obj.type === "i-text") {
        const newSize = Math.round(obj.fontSize * obj.scaleY);
        obj.set({ fontSize: newSize, scaleX: 1, scaleY: 1 });
        obj.setCoords();
        syncStyleFromObject(obj);
      }
    });
  };

  const addText = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !text.trim()) return;

    const itext = new fabric.IText(text, {
      left: canvas.getWidth() / 2,
      top: canvas.getHeight() / 2,
      originX: "center",
      originY: "center",
      fontSize: 30,
      fontWeight: "500",
      fill: "#000000",
      fontFamily: "sans-serif",
      editable: false,
    });

    canvas.add(itext);
    canvas.setActiveObject(itext);
    canvas.renderAll();
    setActiveObject(itext);
    setText("");
  };

  const clearText = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.getObjects().forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.renderAll();
  };

  const Text = (newText) => {
    setText(newText);
    const obj = fabricCanvasRef.current?.getActiveObject();
    if (obj && obj.type === "i-text") {
      obj.set("text", newText);
      fabricCanvasRef.current.renderAll();
    }
  };

  const Color = (color) => {
    const obj = fabricCanvasRef.current?.getActiveObject();
    if (!obj) return;
    obj.set("fill", color);
    fabricCanvasRef.current.renderAll();
    setTextStyle((s) => ({ ...s, fill: color }));
  };

  const Fontsize = (fontSize) => {
    const obj = fabricCanvasRef.current?.getActiveObject();
    if (!obj) return;
    const size = parseInt(fontSize, 10);
    obj.set("fontSize", size);
    obj.setCoords();
    fabricCanvasRef.current.renderAll();
    setTextStyle((s) => ({ ...s, fontSize: size }));
  };

  const Fontweight = (fontWeight) => {
    const obj = fabricCanvasRef.current?.getActiveObject();
    if (!obj) return;
    const isBold = obj.fontWeight === "900" || obj.fontWeight === "bold";
    const next = isBold ? "500" : "900";
    obj.set("fontWeight", next);
    fabricCanvasRef.current.renderAll();
    setTextStyle((s) => ({ ...s, fontWeight: next }));
  };

  const deleteActiveObject = () => {
    const canvas = fabricCanvasRef.current;
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    canvas.remove(obj);
    canvas.discardActiveObject();
    canvas.renderAll();
    setActiveObject(null);
  };

  const addQRCode = async (url) => {
  const canvas = fabricCanvasRef.current;
  if (!canvas || !url || !url.trim()) return;

  // Normalize: prepend https:// if the user didn't
  const fullUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;

  try {
    const qrDataUrl = await QRCode.toDataURL(fullUrl, {
      margin: 1,
      width: 300,
      errorCorrectionLevel: "M",
    });

    const qrImg = await fabric.FabricImage.fromURL(qrDataUrl);

    // Place in bottom-right corner at a modest size
    const targetSize = Math.min(canvas.getWidth(), canvas.getHeight()) * 0.22;
    const scale = targetSize / qrImg.width;

    qrImg.set({
      left: canvas.getWidth() - targetSize - 15,
      top: canvas.getHeight() - targetSize - 15,
      scaleX: scale,
      scaleY: scale,
    });

    canvas.add(qrImg);
    canvas.setActiveObject(qrImg);
    canvas.renderAll();
    setActiveObject(qrImg);
  } catch (err) {
    console.error("QR generation failed:", err);
  }
};

  function generateMeme() {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.renderAll();
    const dataUrl = canvas.toDataURL({ format: "png", quality: 1, multiplier: 2 });
    setImageUrl(dataUrl);
    setShowModal(true);
  }

  function downloadImage() {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "meme.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <EditorContext.Provider
      value={{
        lang,
        Language,
        text,
        Text,
        setText,
        activeObject,
        textStyle,
        registerCanvas,
        addText,
        clearText,
        Color,
        Fontsize,
        Fontweight,
        deleteActiveObject,
        generateMeme,
        downloadImage,
        imageUrl,
        showModal,
        setShowModal,
        addQRCode
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export const useEditorContext = () => useContext(EditorContext);

export { EditorContext, EditorProvider };